/* Left sidebar — primary navigation. Ported from
   design_handoff_daedalus/design_files/sidebar.jsx.

   Desktop-only for now: 240px expanded, 64px collapsed. Collapsed
   state persists in localStorage (daedalus.sbCol) per the plan. */

import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { DaedalusWordmark, DaydWordmark } from '@/components/brand';
import { MOCK_USER } from '@/api/mocks/fixtures';
import { useAuth } from '@/auth/MsalBootstrap';
import { useWhoami } from '@/auth/WhoamiContext';
import {
  assignConversationProject,
  deleteConversation,
  listConversations,
  listMyProjects,
  setConversationTitle,
  type ConversationListItem,
} from '@/api/client';
import { OpenFileModal } from '@/components/OpenFileModal';
import { ThreadRowMenu, type AssignProject } from './ThreadRowMenu';
import { useUnreadCount } from './useUnreadCount';
import {
  RECENT_THREADS_LIMIT,
  THREADS_CHANGED_EVENT,
  notifyThreadsChanged,
  toAssignProjects,
} from './threadList';
import type { ArtifactRef } from '@/api/schemas';

const COORDINATOR_DOMAIN = 'coordinator' as const;

type NavKind = 'home' | 'tools' | 'projects' | 'bids' | 'accounting' | 'portfolio' | 'admin' | 'turnaround' | 'mobile' | 'settings' | 'threads';

interface NavDef {
  to: string;
  label: string;
  kind: NavKind;
  tag?: 'exec' | 'admin' | 'fin';
}

interface UserLike {
  name: string;
  role: string;
  unit: string;
  /** Phase 0.5 mock field. In Phase 1 this comes from PreflightAuthResult. */
  specAccess?: string[];
}

const STORAGE_KEY = 'daedalus.sbCol';

/* SharePoint product mark — monochrome, centered nod to the official
   three-circle cluster. Uses `currentColor` (with opacity layering for
   depth) instead of the brand teal so it matches the rest of the
   sidebar's line icons and inherits the button's ink → accent hover in
   both light and dark themes. Artwork is centered in a 16×16 box so it
   sits flush in the button next to the "New thread" glyph. */
function SharePointIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="6.5" cy="6" r="4" opacity="0.45" />
      <circle cx="10" cy="8" r="3.3" opacity="0.7" />
      <circle cx="7.5" cy="11" r="2.6" />
    </svg>
  );
}

function NavIcon({ kind }: { kind: NavKind }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 16 16',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.25,
  };
  if (kind === 'home')      return <svg {...common}><path d="M2 7 L8 2 L14 7 V13 H10 V9 H6 V13 H2 Z" /></svg>;
  if (kind === 'tools')     return <svg {...common}><rect x="2" y="2" width="5" height="5" /><rect x="9" y="2" width="5" height="5" /><rect x="2" y="9" width="5" height="5" /><rect x="9" y="9" width="5" height="5" /></svg>;
  if (kind === 'projects')  return <svg {...common}><path d="M2 4 H6 L7 6 H14 V13 H2 Z" /></svg>;
  if (kind === 'bids')      return <svg {...common}><path d="M3 3 H13 V13 H3 Z M5 6 H11 M5 9 H11 M5 12 H8" /></svg>;
  if (kind === 'portfolio') return <svg {...common}><path d="M2 13 V8 M6 13 V4 M10 13 V9 M14 13 V2" /></svg>;
  if (kind === 'accounting')return <svg {...common}><path d="M2 6 L8 2 L14 6 M3 6 V12 M6.5 6 V12 M9.5 6 V12 M13 6 V12 M2 13.5 H14" /></svg>;
  if (kind === 'admin')     return <svg {...common}><path d="M8 2 L13 4 V8 C13 11 11 13 8 14 C5 13 3 11 3 8 V4 Z" /></svg>;
  if (kind === 'turnaround')return <svg {...common}><circle cx="8" cy="8.5" r="5.5" /><path d="M8 5 V8.5 L10.5 10 M6 1.5 H10" /></svg>;
  if (kind === 'mobile')    return <svg {...common}><rect x="4" y="2" width="8" height="12" rx="1" /><circle cx="8" cy="12" r=".5" fill="currentColor" /></svg>;
  if (kind === 'threads')   return <svg {...common}><path d="M2.5 3 H13.5 V10.5 H7 L4 13 V10.5 H2.5 Z M5 6 H11 M5 8 H9" /></svg>;
  if (kind === 'settings')  return <svg {...common}><circle cx="8" cy="8" r="2.25" /><path d="M8 1.5 V3 M8 13 V14.5 M1.5 8 H3 M13 8 H14.5 M3.4 3.4 L4.5 4.5 M11.5 11.5 L12.6 12.6 M12.6 3.4 L11.5 4.5 M4.5 11.5 L3.4 12.6" /></svg>;
  return null;
}

/* Collapse/expand glyph: a panel outline with a chevron pointing the way the
   rail will move. Replaces the bare ‹ / › characters. */
function PanelToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" />
      <path d="M6 3 V13" />
      <path d={collapsed ? 'M9.5 6.5 L11 8 L9.5 9.5' : 'M11 6.5 L9.5 8 L11 9.5'} />
    </svg>
  );
}

function NavItem({
  to,
  label,
  kind,
  collapsed,
  tag,
  muted,
  onClick,
}: {
  to: string;
  label: string;
  kind: NavKind;
  collapsed: boolean;
  tag?: string;
  muted?: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      className={({ isActive }) => `ni ${isActive ? 'on' : ''} ${muted ? 'muted' : ''}`}
      title={collapsed ? label : undefined}
    >
      <span className="ni-icon">
        <NavIcon kind={kind} />
      </span>
      {!collapsed && <span className="ni-label">{label}</span>}
      {!collapsed && tag && <span className="ni-tag">{tag}</span>}
    </NavLink>
  );
}

export function Sidebar({
  variant = 'desktop',
  onNavigate,
}: {
  user?: UserLike;
  /** 'drawer' — hosted in the mobile off-canvas drawer (AppShell ≤768px):
      always expanded, no collapse toggle, and every navigation action
      calls onNavigate so the shell can close the drawer. */
  variant?: 'desktop' | 'drawer';
  onNavigate?: () => void;
} = {}) {
  // The identity panel at the bottom reads from useAuth() — that's MSAL
  // in production / dev-hybrid mode, or MOCK_USER in walkthrough mode.
  // Name comes from MSAL claims; the title is resolved from the Entra
  // directory (Microsoft Graph /me?$select=jobTitle) via getJobTitle().
  // When the directory has no title set, the title line is hidden rather
  // than showing a placeholder.
  const { user: authUser, mocked, logout, getAccessToken, getJobTitle } = useAuth();
  // Org job title from Microsoft Graph. null until resolved / when the
  // directory record has no title — the render hides the line in that case.
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [threads, setThreads] = useState<ConversationListItem[]>([]);
  // Retroactive "Add to project" (migration 090): the user's projects,
  // lazy-loaded once for the ThreadRowMenu's Add-to-project picker;
  // assignBusy guards the assign call.
  const [assignProjects, setAssignProjects] = useState<AssignProject[] | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  // Inline rename UI state — only one row at a time. renamingId is the
  // thread being edited; renameDraft is the in-progress text.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameRef = useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openFileOpen, setOpenFileOpen] = useState(false);
  // Bumped when a thread changes elsewhere (the Threads scene, or this
  // list's own row menu) so the recents refetch without a navigation.
  const [refreshKey, setRefreshKey] = useState(0);
  // MOCK_USER is walkthrough-mode only. In real mode an absent user must
  // NEVER borrow the mock persona's name — that's how an unauthenticated
  // shell (pre-guard) showed users signed in as "Rachel Torres". The
  // RequireAuth guard makes the real-mode fallback unreachable; "Signed
  // out" is defense-in-depth in case a future route bypasses the guard.
  const displayUser = {
    name: authUser?.name ?? (mocked ? MOCK_USER.name : 'Signed out'),
  };
  // Unread notification count for the bell badge (shared with the mobile
  // top bar via the useUnreadCount hook).
  const unread = useUnreadCount();

  const [collapsedPref, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  // The drawer always renders the expanded variant — a 64px rail makes no
  // sense inside an overlay, and the expanded markup is what restores the
  // thread list / search / sign-out on mobile.
  const collapsed = variant === 'drawer' ? false : collapsedPref;
  const navigate = useNavigate();
  const location = useLocation();
  // The thread the user is currently viewing — carried in the route state
  // when a thread is resumed from the sidebar (see onResumeThread). Drives
  // the single active-row highlight (replaces the old is_live highlight,
  // which lit up every non-archived thread in the multi-thread model).
  const currentThreadId = (location.state as { threadId?: string } | null)?.threadId ?? null;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsedPref ? '1' : '0');
    } catch {
      /* localStorage may be unavailable (private mode) — non-fatal */
    }
  }, [collapsedPref]);

  useEffect(() => {
    const onChanged = () => setRefreshKey((k) => k + 1);
    window.addEventListener(THREADS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(THREADS_CHANGED_EVENT, onChanged);
  }, []);

  // Resolve the user's org job title from the Entra directory (Microsoft
  // Graph) once we have a signed-in account. Best-effort — getJobTitle
  // returns null on any failure or when no title is set, leaving the line
  // hidden. Re-runs if the signed-in identity changes.
  useEffect(() => {
    let cancelled = false;
    if (!authUser?.oid || !getJobTitle) {
      setJobTitle(null);
      return;
    }
    void getJobTitle().then((title) => {
      if (!cancelled) setJobTitle(title);
    });
    return () => {
      cancelled = true;
    };
  }, [authUser?.oid, getJobTitle]);

  // Load (and refresh) the user's recent threads. Re-runs when the
  // route changes — covers the "user just clicked New Thread / resumed
  // a thread" case (those navigate with a new resetKey) so the sidebar
  // re-reads after the SQL row flips. Also re-runs on refreshKey, when a
  // thread was renamed, deleted or filed somewhere else. Only the most
  // recent few: the full list and its search are the Threads scene.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!authUser?.oid) {
        setThreads([]);
        return;
      }
      try {
        const token = await getAccessToken();
        // Home sidebar shows the user's GLOBAL threads only — project-
        // anchored threads (migration 090) live in their project's Threads
        // tab, not here.
        const list = await listConversations(token, {
          domain: COORDINATOR_DOMAIN,
          scope: 'global',
          limit: RECENT_THREADS_LIMIT,
        });
        if (!cancelled) setThreads(list);
      } catch {
        // Sidebar list is non-critical — fall back to an empty list
        // rather than blocking the rest of the shell from rendering.
        if (!cancelled) setThreads([]);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.oid, location.pathname, location.state, refreshKey]);

  // Gate the privileged nav links on the signed-in user's role flags, resolved
  // once by the shell-level WhoamiProvider (/tools/agents/whoami). Non-execs
  // don't see Portfolio; non-admins don't see Admin. Fail-closed while the
  // lookup resolves (isExec/isAdmin default false); mock mode shows both.
  // The routes themselves are guarded by RequireExec/RequireAdmin (App.tsx) —
  // hiding the link is the UX half; the route guard is the enforcement half.
  const { isExec, isAdmin, isAccounting } = useWhoami();
  const canExec = isExec;
  const canAdmin = isAdmin;
  const canAccounting = isAccounting;

  const main: NavDef[] = [
    { to: '/home',     label: 'Home',     kind: 'home' },
    { to: '/tools',    label: 'Tools',    kind: 'tools' },
    { to: '/projects', label: 'Projects', kind: 'projects' },
    { to: '/bids',     label: 'Precon',   kind: 'bids' },
  ];
  // Accounting is a full domain hub (like Projects), not a leadership chip —
  // so it sits in the PRIMARY nav, shown to accounting staff + execs. Route
  // enforcement is RequireAccounting (App.tsx); the server-side SQL gate on
  // every /tools/accounting/* endpoint is the real security boundary.
  if (canAccounting) main.push({ to: '/accounting', label: 'Accounting', kind: 'accounting' });
  const gated: NavDef[] = [];
  if (canExec)  gated.push({ to: '/portfolio', label: 'Portfolio', kind: 'portfolio', tag: 'exec' });
  // Accounting staff (Controller) get the same route locked to the Financials
  // tab (Portfolio.tsx finOnly) — label it for what they'll actually see.
  else if (canAccounting) gated.push({ to: '/portfolio?tab=financials', label: 'Financials', kind: 'portfolio', tag: 'fin' });
  if (canAdmin) gated.push({ to: '/admin',     label: 'Admin',     kind: 'admin',     tag: 'admin' });
  // Ball-in-court review turnaround — private to management (exec-only shell;
  // /tools/turnaround/* is admin-or-executive at the API).
  if (canExec)  gated.push({ to: '/turnaround', label: 'Turnaround', kind: 'turnaround', tag: 'exec' });

  // Auto-focus the rename input when one opens.
  useEffect(() => {
    if (renamingId && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [renamingId]);

  function beginRename(thread: ConversationListItem) {
    setRenamingId(thread.thread_id);
    setRenameDraft(thread.title ?? '');
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameDraft('');
  }

  async function commitRename(thread_id: string) {
    const trimmed = renameDraft.trim();
    // Optimistic update so the sidebar reflects the new label instantly.
    if (trimmed.length > 0) {
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === thread_id ? { ...t, title: trimmed.slice(0, 60) } : t
        )
      );
    }
    cancelRename();
    if (trimmed.length === 0) return;
    if (!authUser?.oid) return;
    try {
      const token = await getAccessToken();
      await setConversationTitle(token, {
        thread_id,
        title: trimmed.slice(0, 60),
        overwrite: true,
      });
      notifyThreadsChanged();
    } catch (err) {
      console.error('[Sidebar] rename failed:', err);
    }
  }

  async function commitDelete(thread_id: string, save_memory: boolean) {
    if (deletingId) return;
    if (!authUser?.oid) return;
    if (renamingId) cancelRename();
    setDeletingId(thread_id);
    const wasOnlyLive = threads.some(
      (t) => t.thread_id === thread_id && t.is_live
    );
    // Optimistic removal — strip the row immediately so the UI feels
    // responsive even before the network call resolves. If the delete
    // fails the list reload at the end will put it back.
    setThreads((prev) => prev.filter((t) => t.thread_id !== thread_id));
    try {
      const token = await getAccessToken();
      const result = await deleteConversation(token, { thread_id, save_memory });
      // Refetch both lists: the recents pull the next thread up into the
      // freed slot, and the Threads scene drops the row.
      notifyThreadsChanged();
      if (save_memory && result.memories_saved > 0) {
        console.info(
          `[Sidebar] saved ${result.memories_saved} memory row(s) before deleting ${thread_id}`
        );
      }
      // If the user just deleted the thread they were actively
      // chatting in, remount HomeChat so the next preflight +
      // ensure-thread creates a fresh one — otherwise the in-memory
      // threadId points at a Foundry conversation that no longer
      // exists and the next send 404s.
      if (result.was_live || wasOnlyLive) {
        navigate('/home', { state: { resetKey: Date.now() } });
      }
    } catch (err) {
      console.error('[Sidebar] deleteConversation failed:', err);
    } finally {
      setDeletingId(null);
    }
  }

  function onResumeThread(thread_id: string) {
    // Route to /home carrying the thread_id; HomeChat performs the actual
    // resume (make-live + bump) and hydrates it on mount. The resetKey
    // forces a remount so switching threads re-bootstraps cleanly.
    navigate('/home', { state: { resetKey: Date.now(), threadId: thread_id } });
    onNavigate?.();
  }

  // ── Retroactive "Add to project" (migration 090) ──────────────────────

  async function loadAssignProjects() {
    // Lazy-load the user's projects once (the ThreadRowMenu's Add-to-project
    // view calls this when first opened). Access-scoped server-side, so
    // whatever comes back is safe to offer.
    if (assignProjects !== null) return;
    try {
      const token = await getAccessToken();
      setAssignProjects(toAssignProjects(await listMyProjects(token)));
    } catch {
      setAssignProjects([]);
    }
  }

  async function commitAssign(thread_id: string, project_id: number) {
    if (assignBusy) return;
    if (!authUser?.oid) return;
    setAssignBusy(true);
    // Optimistic: a thread filed under a project leaves the global home list.
    setThreads((prev) => prev.filter((t) => t.thread_id !== thread_id));
    try {
      const token = await getAccessToken();
      await assignConversationProject(token, { thread_id, project_id });
      notifyThreadsChanged();
    } catch (err) {
      console.error('[Sidebar] assignConversationProject failed:', err);
      // Re-sync on failure so the row isn't lost if the assign didn't land.
      navigate(location.pathname, { state: { resetKey: Date.now() }, replace: true });
    } finally {
      setAssignBusy(false);
    }
  }

  // Open-file modal fires this when the user picks a file. We don't
  // own HomeChat's artifact list, so we dispatch a custom window
  // event the chat scene listens for + navigate to /home so the
  // chat is what the panel docks next to. Worth the event indirection
  // — it keeps the components decoupled from a shared parent store
  // and avoids touching App.tsx routing.
  function onArtifactPicked(artifact: ArtifactRef) {
    const evt = new CustomEvent('daedalus:open-artifact', { detail: artifact });
    window.dispatchEvent(evt);
    if (location.pathname !== '/home') {
      navigate('/home');
    }
    onNavigate?.();
  }

  function onNewThread() {
    // Lazy new thread (multi-thread model, migration 090): no backend
    // reset/create — just open a blank composer at /home with startNew.
    // Nothing persists until the user sends the first message, so we never
    // leave behind an empty, untitled thread (the old eager-create flow was
    // the source of the untitled-thread clutter).
    navigate('/home', {
      replace: location.pathname === '/home',
      state: { resetKey: Date.now(), startNew: true },
    });
    onNavigate?.();
  }

  return (
    <aside className={`sb ${collapsed ? 'sb--col' : ''}`}>
      <div className="sb-top">
        <div className="sb-brand">
          {/* 16px keeps "dayd" inside the 64px rail (it renders ~44px wide).
              At 22px it was 60px, which pushed the expand button off the
              rail's edge where nobody could reach it. */}
          {collapsed ? <DaydWordmark size={16} /> : <DaedalusWordmark size={22} />}
        </div>
        {variant !== 'drawer' && (
          <button
            type="button"
            className="sb-collapse"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <PanelToggleIcon collapsed={collapsed} />
          </button>
        )}
      </div>

      <button type="button" className="sb-new" onClick={onNewThread}>
        <span className="sb-new-glyph">+</span>
        {!collapsed && <span>New thread</span>}
      </button>

      <button
        type="button"
        className="sb-open-file"
        onClick={() => setOpenFileOpen(true)}
        title="Open a SharePoint file in the side panel"
      >
        <span className="sb-open-file-glyph" aria-hidden>
          <SharePointIcon size={16} />
        </span>
        {!collapsed && <span>SharePoint</span>}
      </button>

      <NavLink
        to="/notifications"
        onClick={onNavigate}
        className={({ isActive }) => `sb-bell ${isActive ? 'on' : ''}`}
        title="Notifications"
      >
        <span className="sb-bell-glyph" aria-hidden>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25">
            <path d="M4 7 a4 4 0 0 1 8 0 c0 3 1 4 1 4 H3 s1 -1 1 -4 Z" />
            <path d="M6.5 13 a1.5 1.5 0 0 0 3 0" />
          </svg>
        </span>
        {!collapsed && <span className="sb-bell-label">Notifications</span>}
        {unread > 0 && (
          <span className="sb-bell-badge" aria-label={`${unread} unread`}>
            {/* The rail's bell button is 40px wide, so "99+" would cover the
                bell. Collapsed caps at "9+"; the label keeps the count. */}
            {collapsed && unread > 9 ? '9+' : unread > 99 ? '99+' : unread}
          </span>
        )}
      </NavLink>

      <OpenFileModal
        open={openFileOpen}
        onClose={() => setOpenFileOpen(false)}
        onPick={onArtifactPicked}
      />

      <nav className="sb-nav">
        {main.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} onClick={onNavigate} />
        ))}
      </nav>

      {/* Collapsed, the recents are hidden, so the rail gets one way into
          the full thread list. */}
      {collapsed && authUser?.oid && (
        <div className="sb-threads-col">
          <NavItem to="/threads" label="All threads" kind="threads" collapsed onClick={onNavigate} />
        </div>
      )}

      {!collapsed && authUser?.oid && (
        <div className="sb-recents">
          <div className="sb-section">Recent threads</div>
          <div className="sb-recents-list">
            {threads.map((t) => {
            // The active highlight tracks the thread the user is CURRENTLY
            // viewing (carried in the route state when a thread is resumed),
            // NOT is_live — in the multi-thread model many threads are
            // non-archived at once, so is_live would light up several rows.
            const isCurrent = currentThreadId != null && t.thread_id === currentThreadId;
            const label = (t.title && t.title.trim().length > 0) ? t.title : 'Untitled';
            const isRenaming = renamingId === t.thread_id;
            const isDeleting = deletingId === t.thread_id;
            const rowClass = [
              'sb-recent-row',
              isCurrent ? 'is-active' : '',
              isDeleting ? 'is-pending' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div key={t.thread_id} className={rowClass}>
                {/* Active-thread indicators. Both always mount on the
                    row; CSS shows them only when the row carries
                    ``is-active``. Keeping them in the DOM lets the
                    center-outward grow animation transition smoothly
                    when the active thread changes. */}
                <span className="sb-recent-glow" aria-hidden />
                <span className="sb-recent-progress" aria-hidden />
                {isRenaming ? (
                  <input
                    ref={renameRef}
                    className="sb-recent-input"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => void commitRename(t.thread_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void commitRename(t.thread_id);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRename();
                      }
                    }}
                    maxLength={60}
                    placeholder="Thread name"
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      className={`sb-recent ${t.is_live ? '' : 'sb-recent-muted'}`}
                      title={label}
                      disabled={isDeleting}
                      onClick={() => onResumeThread(t.thread_id)}
                    >
                      {label}
                    </button>
                    <ThreadRowMenu
                      onRename={() => beginRename(t)}
                      onDelete={(save) => void commitDelete(t.thread_id, save)}
                      projects={assignProjects}
                      onOpenAssign={() => void loadAssignProjects()}
                      onAssign={(pid) => void commitAssign(t.thread_id, pid)}
                      busy={isDeleting || assignBusy}
                    />
                  </>
                )}
              </div>
            );
          })}
          </div>
          <NavLink
            to="/threads"
            end
            onClick={onNavigate}
            className={({ isActive }) => `sb-all ${isActive ? 'on' : ''}`}
          >
            <span className="ni-icon">
              <NavIcon kind="threads" />
            </span>
            <span className="sb-all-label">All threads</span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
              <path d="M3 8 H13 M9 4 L13 8 L9 12" />
            </svg>
          </NavLink>
        </div>
      )}

      {gated.length > 0 && (
        <div className="sb-gated">
          {!collapsed && <div className="sb-section">Admin &amp; leadership</div>}
          {gated.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} onClick={onNavigate} />
          ))}
        </div>
      )}

      <div className="sb-settings">
        <NavItem to="/settings" label="Settings" kind="settings" collapsed={collapsed} onClick={onNavigate} />
      </div>

      <div className="sb-user">
        <div className="sb-user-row">
          <div className="sb-avatar">
            {displayUser.name
              .split(' ')
              .map((w) => w[0])
              .join('')}
          </div>
          {!collapsed && (
            <div className="sb-user-meta">
              <div className="sb-user-name">{displayUser.name}</div>
              {jobTitle && (
                <div className="sb-user-role mono-lc">{jobTitle}</div>
              )}
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            className="sb-signout"
            onClick={async () => {
              // Call MSAL logout — navigates to Microsoft's logout endpoint,
              // clears local token cache, and redirects back to the app's
              // postLogoutRedirectUri (/sign-in). In walkthrough mode
              // MockBootstrap.logout just clears user state in-memory.
              try {
                await logout();
              } catch (err) {
                console.error('[MSAL] logout failed:', err);
              }
              navigate('/sign-in', { replace: true });
            }}
          >
            Sign out
          </button>
        )}
      </div>

      <style>{`
        .sb {
          width: 240px; flex-shrink: 0;
          min-height: 0;
          /* A short viewport overflows the rail; without this the overflow
             is clipped, taking the user footer with it. The rail scrolls
             instead. overflow-x is pinned because overflow-y: auto makes it
             auto too, and anything a few px too wide would then put a
             horizontal scrollbar on the 64px rail. */
          overflow-y: auto; overflow-x: hidden;
          background: var(--bg-2); border-right: 1px solid var(--rule-soft);
          display: flex; flex-direction: column;
          transition: width .22s ease;
        }
        .sb--col { width: 64px; }
        .sb-top {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 14px 10px; border-bottom: 1px solid var(--rule-hair);
        }
        /* Collapsed: the mark sits above the toggle. Side by side they need
           ~80px and the rail has 64. */
        .sb--col .sb-top {
          flex-direction: column; justify-content: center; gap: 10px;
          padding: 14px 0 12px;
        }
        .sb-brand { display: flex; align-items: center; }
        .sb-collapse {
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; border: 1px solid var(--rule-hair);
          width: 28px; height: 28px; padding: 0; flex-shrink: 0;
          color: var(--ink-2); cursor: pointer;
        }
        .sb-collapse:hover { border-color: var(--accent); color: var(--accent); }

        .sb-new {
          margin: 12px; padding: 10px 12px;
          display: flex; gap: 8px; align-items: center; justify-content: flex-start;
          background: var(--ink); color: var(--bg-1);
          border: 0; font: inherit; font-size: 13px; cursor: pointer;
        }
        .sb-new:hover { background: var(--accent); }
        .sb--col .sb-new { padding: 10px 0; justify-content: center; }
        .sb-new-glyph { font-size: 16px; line-height: 1; }

        .sb-open-file {
          margin: 0 12px 12px; padding: 8px 12px;
          display: flex; gap: 8px; align-items: center; justify-content: flex-start;
          background: transparent; color: var(--ink-2);
          border: 1px solid var(--rule-soft);
          font: inherit; font-size: 13px; cursor: pointer;
        }
        .sb-open-file:hover { border-color: var(--accent); color: var(--accent); }
        .sb--col .sb-open-file { padding: 8px 0; justify-content: center; }
        .sb-open-file-glyph {
          display: inline-flex; align-items: center; justify-content: center;
          width: 16px; height: 16px; flex-shrink: 0;
          line-height: 1;
        }

        .sb-bell {
          position: relative;
          margin: 0 12px 12px; padding: 8px 12px;
          display: flex; gap: 8px; align-items: center; justify-content: flex-start;
          background: transparent; color: var(--ink-2);
          border: 1px solid var(--rule-soft);
          font: inherit; font-size: 13px; cursor: pointer;
          text-decoration: none;
        }
        .sb-bell:hover { border-color: var(--accent); color: var(--accent); }
        .sb-bell.on { border-color: var(--accent); color: var(--accent); }
        .sb--col .sb-bell { padding: 8px 0; justify-content: center; }
        .sb-bell-glyph {
          display: inline-flex; align-items: center; justify-content: center;
          width: 16px; height: 16px; flex-shrink: 0; line-height: 1;
        }
        .sb-bell-label { flex: 1; }
        .sb-bell-badge {
          min-width: 16px; height: 16px; padding: 0 4px; box-sizing: border-box;
          display: inline-flex; align-items: center; justify-content: center;
          background: var(--accent); color: var(--bg-1);
          border-radius: 8px; font-size: 10px; font-weight: 600; line-height: 1;
        }
        /* Collapsed: hang the badge off the button's top-right corner, clear
           of the bell glyph. The ring separates it from the button border. */
        .sb--col .sb-bell-badge {
          position: absolute; top: -8px; right: -8px;
          min-width: 20px; height: 18px; border-radius: 9px;
          border: 2px solid var(--bg-2); font-size: 9px;
        }

        .sb-nav { display: flex; flex-direction: column; padding: 0 8px; }

        .sb-section {
          padding: 14px 12px 6px;
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--ink-3);
        }
        /* The recents list sits at its natural height with no inner scroll;
           the full list and its search are the Threads scene (/threads). */
        .sb-recents { padding: 0 8px 8px; }
        .sb-recents-list { display: flex; flex-direction: column; }
        .sb-all {
          display: flex; gap: 10px; align-items: center;
          margin: 6px 0 0; padding: 7px 12px;
          border-left: 2px solid transparent;
          font-size: 13px; font-weight: 500; color: var(--ink-2);
          text-decoration: none;
        }
        .sb-all:hover { background: var(--bg-1); color: var(--accent); }
        .sb-all.on { background: var(--bg-1); color: var(--accent); border-left-color: var(--accent); }
        .sb-all-label { flex: 1; }
        .sb-threads-col { padding: 4px 8px; border-top: 1px dashed var(--rule-hair); }

        .sb-recent-row {
          position: relative;
          display: flex; align-items: stretch;
          margin: 1px 0;
          padding-left: 2px;          /* breathing room for the animated bar */
          background: transparent;
          isolation: isolate;
        }
        .sb-recent-row .sb-recent {
          display: block; flex: 1; min-width: 0; text-align: left;
          background: transparent; border: 0; padding: 6px 10px;
          font: inherit; font-size: 13px; color: var(--ink-2); cursor: pointer;
          border-left: 2px solid transparent;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sb-recent:hover { color: var(--accent); }
        .sb-recent-muted { color: var(--ink-3); }

        .sb-recent-input {
          flex: 1; min-width: 0;
          background: var(--bg-1); color: var(--ink);
          border: 1px solid var(--accent);
          padding: 5px 10px; margin-left: 2px;
          font: inherit; font-size: 13px;
          outline: 0;
        }

        /* Active-thread highlight: a soft tinted background plus an
           animated marching-glow bar on the left edge. The glow uses a
           gradient that shifts vertically via background-position
           animation — pure CSS, GPU-accelerated, no JS required. */
        .sb-recent-row.is-active {
          background: linear-gradient(
            to right,
            color-mix(in srgb, var(--accent) 12%, transparent),
            transparent 70%
          );
        }
        .sb-recent-row.is-active .sb-recent {
          color: var(--accent);
        }
        /* ── Active-thread indicators ─────────────────────────────
           Two elements that only show on .is-active rows:

           .sb-recent-glow      thin accent bar along the LEFT edge.
                                Vertical gradient continuously flows
                                bottom-to-top (forward-only loop).
           .sb-recent-progress  full-width accent bar pinned to the
                                BOTTOM edge. When the row becomes
                                active, the bar scales in from the
                                CENTER outward (transform-origin:
                                center; scaleX 0 → 1) and stays
                                until another thread becomes active.
                                When this row becomes inactive again
                                (another thread clicked), the bar
                                collapses back toward the center
                                via the reverse transition.

           Both elements are always rendered in the DOM so the CSS
           transitions actually fire when class state flips —
           conditional rendering would unmount and skip the anim. */

        /* Static accent line on the left edge with a brighter
           pulse that travels up and down it (a "heartbeat in a
           vein" — like an Equalizer-style cursor scrubber). The
           base bar is a flat color; the moving glow is a
           pseudo-element clipped by the bar's overflow:hidden. */
        .sb-recent-glow {
          position: absolute; left: 0; top: 2px; bottom: 2px;
          width: 2px;
          background: color-mix(in srgb, var(--accent) 35%, transparent);
          opacity: 0;
          overflow: hidden;
          transition: opacity 300ms ease-out;
          pointer-events: none;
        }
        .sb-recent-row.is-active .sb-recent-glow { opacity: 1; }
        .sb-recent-glow::after {
          content: '';
          position: absolute;
          left: 0; right: 0;
          height: 30%;
          top: 0%;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            color-mix(in srgb, var(--accent) 90%, white) 50%,
            transparent 100%
          );
        }
        .sb-recent-row.is-active .sb-recent-glow::after {
          /* animation-direction: alternate gives us 0 → 70 → 0 in
             one keyframe block; ease-in-out smooths the turnaround
             at top + bottom so it reads as a continuous
             oscillation rather than a snap. 70% is the lowest the
             pulse can sit and still stay fully inside the 2px bar
             (since the pulse itself is 30% of bar height). */
          animation: sb-recent-glow-pulse 2.4s ease-in-out infinite alternate;
        }
        @keyframes sb-recent-glow-pulse {
          from { top: 0%; }
          to   { top: 70%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sb-recent-row.is-active .sb-recent-glow::after {
            animation: none; top: 35%;
          }
        }

        .sb-recent-progress {
          position: absolute; left: 0; right: 0; bottom: 0;
          height: 2px;
          pointer-events: none;
          background: var(--accent);
          transform: scaleX(0);
          transform-origin: center center;
          opacity: 0;
          transition:
            transform 360ms cubic-bezier(0.2, 0.7, 0.2, 1),
            opacity 280ms ease-out;
        }
        .sb-recent-row.is-active .sb-recent-progress {
          transform: scaleX(1);
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .sb-recent-progress { transition: none; }
        }

        .sb-recent-row.is-pending .sb-recent { opacity: 0.65; }

        .sb-gated { padding: 0 8px 4px; border-top: 1px dashed var(--rule-hair); }
        .sb-settings { padding: 4px 8px; border-top: 1px dashed var(--rule-hair); }

        .ni {
          display: flex; gap: 10px; align-items: center;
          width: 100%; text-align: left;
          padding: 8px 12px; margin: 1px 0;
          background: transparent; border: 0;
          font: inherit; font-size: 14px; color: var(--ink);
          cursor: pointer;
          border-left: 2px solid transparent;
          position: relative;
          text-decoration: none;
        }
        .ni:hover { background: var(--bg-1); }
        .ni.on { background: var(--bg-1); color: var(--accent); border-left-color: var(--accent); }
        .ni.muted { color: var(--ink-3); }
        .ni-icon {
          display: flex; align-items: center; justify-content: center;
          width: 20px; height: 20px; flex-shrink: 0; color: currentColor;
        }
        .ni-label { flex: 1; }
        .ni-tag {
          font-family: 'JetBrains Mono', monospace; font-size: 9px;
          letter-spacing: 0.08em; text-transform: uppercase;
          padding: 2px 6px; border: 1px solid var(--rule-hair); color: var(--ink-3);
        }
        .sb--col .ni { justify-content: center; padding: 10px 0; }

        .sb-user {
          margin-top: auto; padding: 14px 12px;
          border-top: 1px solid var(--rule-hair); background: var(--bg-1);
        }
        .sb-user-row { display: flex; gap: 10px; align-items: center; }
        /* Collapsed rail: center the avatar in the 64px column so it lines
           up under the nav icons / New-thread / SharePoint buttons above it
           (all of which center via .sb--col). Without this the circle stays
           left-pinned and reads as misaligned. */
        .sb--col .sb-user-row { justify-content: center; gap: 0; }
        .sb-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--accent); color: var(--bg-1);
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 500; flex-shrink: 0;
        }
        .sb-user-meta { min-width: 0; }
        .sb-user-name { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sb-user-role { color: var(--ink-3); font-size: 10px; }
        .sb-signout {
          margin-top: 10px; width: 100%; padding: 6px 8px;
          background: transparent; border: 1px solid var(--rule-hair);
          font: inherit; font-size: 11px; color: var(--ink-2); cursor: pointer;
          font-family: 'JetBrains Mono', monospace; text-transform: uppercase; letter-spacing: 0.08em;
        }
        .sb-signout:hover { border-color: var(--accent); color: var(--accent); }
        .sb--col .sb-user { padding: 10px 8px; }
      `}</style>
    </aside>
  );
}
